import classNames from "classnames";
import { html } from "lit-html";
import i18next from "i18next";
import { useRef } from "haunted";
import { useEffectWithoutCleanup, useMemo, useState } from "Shared/haunted/CustomHooks";
import { ref } from "Components/directives/ref";
import {
	CHILE_COUNTRY_CODE,
	COLOMBIA_COUNTRY_CODE,
	COLOMBIAN_CULTURE_CODE,
	MIN_NUMBER_FOR_GROUP_BOOKINGS,
} from "Services/constants";
import { CLASS_NAMES } from "Services/ClassNames";
import { isTabletOrMobileResolution } from "Services/common";
import { PUB_SUBS } from "Services/pub-sub-service/PubSub";
import { passengersUtils, PaxType, WarningKey } from "./passengersUtils";
import { CurrentSearchManager } from "Shared/managers/current-search-manager/useCurrentSearchManager";
import { SearchPassengers } from "../../../../../shared/managers/current-search-manager/CurrentSearch";
import {
	DisabledPassengerCountStatesDto,
	PassengerSelectorActionsDto,
	WarningsDto,
	useMobileSearchboxPassengerSelectorModal,
} from "./useMobileSearchboxPassengerSelectorModal";

const { createWarningsMap } = passengersUtils();

export interface Props {
	currentSearchManager: CurrentSearchManager;
	culture: string;
}

export const usePassengerSelector = (props: Props) => {
	const { adults, teens, children, infants } = props.currentSearchManager.currentSearch.passengers;

	const root = useRef<HTMLDivElement>(null);
	const dropdownElem = useRef<HTMLDivElement>(null);

	const [isDesktopDropdownOpen, setIsDesktopDropdownOpen] = useState(false);
	const [isWithGroupBookingWarning, setIsWithGroupBookingWarning] = useState(false);
	const [isWithTeenInfantRemoveWarning, setIsWithTeenInfantRemoveWarning] = useState(false);
	const [warningKeys, setWarningKeys] = useState<WarningKey[]>([]);

	const total = adults + teens + children + infants;

	const isColombianDomesticFlight = useMemo(
		() =>
			props.currentSearchManager.currentSearch.originCity?.countryCode === COLOMBIA_COUNTRY_CODE &&
			props.currentSearchManager.currentSearch.destinationCity?.countryCode === COLOMBIA_COUNTRY_CODE,
		[
			props.currentSearchManager.currentSearch.originCity?.countryCode,
			props.currentSearchManager.currentSearch.destinationCity?.countryCode,
		]
	);

	const maxChildrenAmount = (passengers: SearchPassengers) => {
		if (passengers.adults > 0) return Number.POSITIVE_INFINITY;

		const retVal = passengers.teens - passengers.infants;

		return retVal > 0 ? retVal : 0;
	};

	const maxInfantAmount = (passengers: SearchPassengers) => {
		if (passengers.adults > 0) return passengers.adults + passengers.teens;

		const retVal = passengers.teens - passengers.children;

		return retVal > 0 ? retVal : 0;
	};

	const isChileanSearch = () =>
		props.currentSearchManager.currentSearch.originCity?.countryCode === CHILE_COUNTRY_CODE ||
		props.currentSearchManager.currentSearch.destinationCity?.countryCode === CHILE_COUNTRY_CODE;

	const isAtGroupLimit = () => adults + teens + children === MIN_NUMBER_FOR_GROUP_BOOKINGS - 1;
	const isAddAdultsDisabled = () => isAtGroupLimit();
	const isAddTeensDisabled = () => isAtGroupLimit();
	const isAddChildrenDisabled = () =>
		children >= maxChildrenAmount(props.currentSearchManager.currentSearch.passengers) || isAtGroupLimit();
	const isRemoveAdultsDisabled = () => (adults === 1 && teens === 0) || adults === 0;
	const isRemoveTeensDisabled = () => (adults === 0 && teens === 1) || teens === 0;
	const isRemoveChildrenDisabled = () => children === 0;
	const isAddInfantsDisabled = () => infants >= maxInfantAmount(props.currentSearchManager.currentSearch.passengers);
	const isRemoveInfantsDisabled = () => infants === 0;

	const warningsMap = createWarningsMap(props.culture, isChileanSearch());

	const initDesktopDropdown = () => {
		const onClickOutside = (e: any) => {
			const path = e.path || (e.composedPath && e.composedPath());
			const isClickInside = path.indexOf(dropdownElem.current) > -1 || path.indexOf(root.current) > -1;

			if (!isClickInside) setIsDesktopDropdownOpen(false);
		};

		const onScroll = () => setIsDesktopDropdownOpen(false);

		document.addEventListener("click", onClickOutside);
		document.addEventListener("scroll", onScroll);

		return () => {
			document.removeEventListener("click", onClickOutside);
			document.removeEventListener("scroll", onScroll);
		};
	};

	const checkForGroupBookingWarning = (type: PaxType) => {
		if (type !== "infants" && isAtGroupLimit()) {
			setIsWithGroupBookingWarning(true);
			return;
		}

		setIsWithGroupBookingWarning(false);
	};

	const checkForTeenInfantRemoveWarning = (type: PaxType) => {
		if (type !== "adults" && type !== "teens") {
			setIsWithTeenInfantRemoveWarning(false);
			return;
		}

		const adultsNew = type === "adults" ? adults - 1 : adults;
		const teensNew = type === "teens" ? teens - 1 : teens;

		const newPassengers = {
			...props.currentSearchManager.currentSearch.passengers,
			adults: adultsNew,
			teens: teensNew,
		};

		if (infants > maxInfantAmount(newPassengers)) {
			setIsWithTeenInfantRemoveWarning(true);
			return;
		}

		setIsWithTeenInfantRemoveWarning(false);
	};

	const increasePaxNumber = (type: PaxType, isDisabled: boolean) => {
		if (isDisabled) {
			checkForGroupBookingWarning(type);
			return;
		}

		setIsWithGroupBookingWarning(false);
		setIsWithTeenInfantRemoveWarning(false);

		props.currentSearchManager.setPassengers({
			...props.currentSearchManager.currentSearch.passengers,
			[type]: props.currentSearchManager.currentSearch.passengers[type] + 1,
		});
	};

	const decreasePaxNumber = (type: PaxType, isDisabled: boolean) => {
		setIsWithGroupBookingWarning(false);
		checkForTeenInfantRemoveWarning(type);

		if (isDisabled) return;

		props.currentSearchManager.setPassengers({
			...props.currentSearchManager.currentSearch.passengers,
			[type]: props.currentSearchManager.currentSearch.passengers[type] - 1,
		});
	};

	// Mobile modal
	const disabledStatesDto: DisabledPassengerCountStatesDto = {
		isAddAdultsDisabled: isAddAdultsDisabled(),
		isAddTeensDisabled: isAddTeensDisabled(),
		isAddChildrenDisabled: isAddChildrenDisabled(),
		isAddInfantsDisabled: isAddInfantsDisabled(),
		isRemoveAdultsDisabled: isRemoveAdultsDisabled(),
		isRemoveTeensDisabled: isRemoveTeensDisabled(),
		isRemoveChildrenDisabled: isRemoveChildrenDisabled(),
		isRemoveInfantsDisabled: isRemoveInfantsDisabled(),
	};

	const actionsDto: PassengerSelectorActionsDto = {
		onIncreasePaxNumber: increasePaxNumber,
		onDecreasePaxNumber: decreasePaxNumber,
		onConfirm: (e: MouseEvent) => {
			mobilePassengerSelectorModal.close(e);
		},
	};

	const warningsDto: WarningsDto = {
		isWithGroupBookingWarning,
		isWithTeenInfantRemoveWarning,
		isColombianDomesticFlight,
		isChileanSearch: isChileanSearch(),
	};

	const mobilePassengerSelectorModal = useMobileSearchboxPassengerSelectorModal({
		culture: props.culture,
		passengers: props.currentSearchManager.currentSearch.passengers,
		warnings: warningsDto,
		disabledStates: disabledStatesDto,
		actions: actionsDto,
	});

	const handleMobileInputClick = (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		PUB_SUBS.MobileSearchboxModalOpened.publish({});
		mobilePassengerSelectorModal.open();
	};

	useEffectWithoutCleanup(() => {
		const newPassengers = { ...props.currentSearchManager.currentSearch.passengers };

		if (newPassengers.infants > maxInfantAmount(newPassengers)) {
			newPassengers.infants = maxInfantAmount(newPassengers);
		}
		if (newPassengers.children > maxChildrenAmount(newPassengers)) {
			newPassengers.children = maxChildrenAmount(newPassengers);
		}

		if (
			newPassengers.infants !== props.currentSearchManager.currentSearch.passengers.infants ||
			newPassengers.children !== props.currentSearchManager.currentSearch.passengers.children
		) {
			props.currentSearchManager.setPassengers(newPassengers);
		}
	}, [adults, teens, children, infants]);

	useEffectWithoutCleanup(() => {
		const newWarnings: WarningKey[] = [];

		if (isWithGroupBookingWarning) newWarnings.push("groupBooking");

		if (isWithTeenInfantRemoveWarning) newWarnings.push("teenInfantRemove");

		if (children > 0 || infants > 0) newWarnings.push("minorOrInfantAge");

		if (children > 0 && isColombianDomesticFlight && props.culture.toLowerCase() === COLOMBIAN_CULTURE_CODE) {
			newWarnings.push("childrenAgeWarningColombia");
		}

		if (teens > 0) newWarnings.push("teenLegalGuardian");

		setWarningKeys(newWarnings);
	}, [props.currentSearchManager.currentSearch.passengers, isWithGroupBookingWarning, isWithTeenInfantRemoveWarning]);

	useEffectWithoutCleanup(() => {
		setIsWithGroupBookingWarning(false);
		setIsWithTeenInfantRemoveWarning(false);
	}, [isDesktopDropdownOpen, mobilePassengerSelectorModal.isOpen]);

	useEffectWithoutCleanup(initDesktopDropdown, [isDesktopDropdownOpen]);

	const labelTemplate = (label: string) =>
		html`<div class="font-body text-base font-bold text-n-blue">${label}</div>`;

	const infoTemplate = (info: string) => html`<div class="font-body text-xs text-[#a1a1a1]">${info}</div>`;

	const amountTemplate = (amount: number) =>
		html`<div class="flex h-full w-full items-center justify-center font-body text-sm font-bold">${amount}</div>`;

	const adultsTemplate = () => html`
		<div class="mb-4 flex w-full items-center justify-between">
			<div>${labelTemplate(i18next.t("Adultos"))} ${infoTemplate(i18next.t("18 o más años"))}</div>
			<div class="grid grid-cols-[24px_24px_24px] grid-rows-[24px]">
				<ac-change-passenger-number-button
					.type=${"minus"}
					.isDisabled=${isRemoveAdultsDisabled()}
					.onClick=${() => decreasePaxNumber("adults", isRemoveAdultsDisabled())}
				></ac-change-passenger-number-button>
				${amountTemplate(adults)}
				<ac-change-passenger-number-button
					.type=${"plus"}
					.isDisabled=${isAddAdultsDisabled()}
					.onClick=${() => increasePaxNumber("adults", isAddAdultsDisabled())}
				></ac-change-passenger-number-button>
			</div>
		</div>
	`;

	const teensTemplate = () => html`
		<div class="mb-4 flex w-full items-center justify-between">
			<div>
				${labelTemplate(i18next.t("Adolescentes"))}
				${isChileanSearch() ? infoTemplate(i18next.t("14-17 años")) : infoTemplate(i18next.t("13-17 años"))}
			</div>
			<div class="grid grid-cols-[24px_24px_24px] grid-rows-[24px]">
				<ac-change-passenger-number-button
					.type=${"minus"}
					.isDisabled=${isRemoveTeensDisabled()}
					.onClick=${() => decreasePaxNumber("teens", isRemoveTeensDisabled())}
				></ac-change-passenger-number-button>
				${amountTemplate(teens)}
				<ac-change-passenger-number-button
					.type=${"plus"}
					.isDisabled=${isAddTeensDisabled()}
					.onClick=${() => increasePaxNumber("teens", isAddTeensDisabled())}
				></ac-change-passenger-number-button>
			</div>
		</div>
	`;

	const childrenTemplate = () => html`
		<div class="mb-4 flex w-full items-center justify-between">
			<div>
				${labelTemplate(i18next.t("Niños"))}
				${isChileanSearch() ? infoTemplate(i18next.t("2-13 años")) : infoTemplate(i18next.t("2-12 años"))}
			</div>
			<div class="grid grid-cols-[24px_24px_24px] grid-rows-[24px]">
				<ac-change-passenger-number-button
					.type=${"minus"}
					.isDisabled=${isRemoveChildrenDisabled()}
					.onClick=${() => decreasePaxNumber("children", isRemoveChildrenDisabled())}
				></ac-change-passenger-number-button>
				${amountTemplate(children)}
				<ac-change-passenger-number-button
					.type=${"plus"}
					.isDisabled=${isAddChildrenDisabled()}
					.onClick=${() => increasePaxNumber("children", isAddChildrenDisabled())}
				></ac-change-passenger-number-button>
			</div>
		</div>
	`;

	const infantsTemplate = () => html`
		<div class="mb-4 flex w-full items-center justify-between">
			<div>${labelTemplate(i18next.t("Infantes"))} ${infoTemplate(i18next.t("0-23 meses"))}</div>
			<div class="grid grid-cols-[24px_24px_24px] grid-rows-[24px]">
				<ac-change-passenger-number-button
					.type=${"minus"}
					.isDisabled=${isRemoveInfantsDisabled()}
					.onClick=${() => decreasePaxNumber("infants", isRemoveInfantsDisabled())}
				></ac-change-passenger-number-button>
				${amountTemplate(infants)}
				<ac-change-passenger-number-button
					.type=${"plus"}
					.isDisabled=${isAddInfantsDisabled()}
					.onClick=${() => increasePaxNumber("infants", isAddInfantsDisabled())}
				></ac-change-passenger-number-button>
			</div>
		</div>
	`;

	const submitTemplate = () => html`
		<div
			class="flex w-full cursor-pointer items-center justify-center rounded-full bg-n-cyan py-3 font-body text-sm font-semibold text-white hover:bg-n-blue"
			@click=${() => setIsDesktopDropdownOpen(false)}
		>
			<div class="flex items-center justify-center">
				${i18next.t("Confirmar")} <i class="jsh-circle-chevron-right1 ml-2 mt-px text-sm"></i>
			</div>
		</div>
	`;

	const warningsTemplate = () =>
		warningKeys.length > 0
			? html` <div class="mt-2 flex flex-col items-start">
					${warningKeys.map(
						(key) => html` <div class="mt-2 font-body text-xs text-n-blue">${warningsMap.get(key)}</div> `
					)}
			  </div>`
			: "";

	const dropdownTemplate = () => html`
		<div
			ref=${ref(dropdownElem)}
			class="max-h-full w-[272px] max-w-full flex-row items-stretch overflow-hidden overflow-y-scroll rounded-lg bg-white p-4"
		>
			${adultsTemplate()} ${teensTemplate()} ${childrenTemplate()} ${infantsTemplate()} ${submitTemplate()}
			${warningsTemplate()}
		</div>
	`;

	const inputTemplate = () => html`
		<div
			class=${classNames(
				"flex w-full cursor-pointer items-center justify-between rounded-lg border border-solid bg-white px-4 h-[42px]",
				CLASS_NAMES.FLIGHT_CHANGE_SEARCH_PASSENGER_SELECTOR_CLASS,
				{
					"border-n-cyan": isDesktopDropdownOpen,
					"border-n-blue": !isDesktopDropdownOpen,
				}
			)}
			@click=${() => setIsDesktopDropdownOpen(!isDesktopDropdownOpen)}
		>
			<div class="font-body text-base text-n-blue">
				${total} ${total === 1 ? i18next.t("pasajero") : i18next.t("pasajeros")}
			</div>
			<i class="jsh-user text-base text-[#c1c1c1]"></i>
		</div>
	`;

	const mobileInputTemplate = () => html`
		<div
			class=${classNames(
				"flex h-[34px] w-full cursor-pointer items-center justify-between rounded-lg border border-solid bg-white px-4",
				"border-n-blue"
			)}
			@click=${handleMobileInputClick}
		>
			<div class="font-body text-base text-n-blue">
				${total} ${total === 1 ? i18next.t("pasajero") : i18next.t("pasajeros")}
			</div>
			<i class="jsh-user text-base text-[#c1c1c1]"></i>
		</div>
		${mobilePassengerSelectorModal.htmlTemplate()}
	`;

	const desktopDropdownTemplate = () => html`
		<div ref=${ref(root)} class="relative">
			${inputTemplate()}
			<ac-dropdown2 .content=${dropdownTemplate()} .isOpen=${isDesktopDropdownOpen}></ac-dropdown2>
		</div>
	`;

	const htmlTemplate = () => (isTabletOrMobileResolution() ? mobileInputTemplate() : desktopDropdownTemplate());

	return {
		htmlTemplate,
		open: () => {
			if (isTabletOrMobileResolution()) {
				PUB_SUBS.MobileSearchboxModalOpened.publish({});
				mobilePassengerSelectorModal.open();
			} else {
				setIsDesktopDropdownOpen(true);
			}
		},
	};
};
